//这个文件封装ws的客户端，不包含业务。只需要遵循消息规范
//发送消息：包含topic字段

import ReconnectingWebSocket from "reconnecting-websocket";
import { browser } from "$app/environment";

// WebSocket 实例缓存
const sockets = new Map();

// 默认配置
const DEFAULT_OPTIONS = {
  maxReconnectionDelay: 10000,
  minReconnectionDelay: 1000,
  reconnectionDelayGrowFactor: 1.3,
  connectionTimeout: 4000,
  maxRetries: 5, // 最大重连次数
  heartbeatInterval: 30000,
  enableHeartbeat: true,
  // 队列状态相关配置
  debug: false,
  enableDetailedQueueInfo: false, // 是否启用详细队列信息
  logQueueOperations: false, // 是否记录队列操作日志
  includeQueueInStatus: true, // 状态更新时是否包含队列信息
  maxQueueSize: 1000, // 最大队列长度
};

// 连接状态枚举
const CONNECTION_STATUS = {
  CONNECTING: "connecting",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  ERROR: "error",
  MAX_RETRIES_REACHED: "max_retries_reached", // 达到最大重连次数
};

// 心跳命令
const HEARTBEAT_COMMANDS = {
  PING: "PING",
  PONG: "PONG",
  PING_CODE: 20000,
};

/**
 * 创建WebSocket连接
 * @param {string} path - WebSocket路径
 * @param {boolean} isIm - 是否为即时通讯服务
 * @param {object} options - 配置选项
 */
export const createSocket = (path = "/ws", isIm = false, options = {}) => {
  // 如果已存在实例，直接返回
  if (sockets.has(path)) {
    return sockets.get(path);
  }

  // 合并配置
  const config = { ...DEFAULT_OPTIONS, ...options };

  // 构造WebSocket URL
  const url = browser && isIm ? path : browser ? `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${path}` : "";

  // 使用rune状态管理
  let wsState = $state({
    status: CONNECTION_STATUS.CONNECTING,
    reconnectAttempts: 0,
    lastError: null,
    connected: false,
  });

  // 监听器管理
  const listeners = new Set();
  const errorListeners = new Set();
  const statusListeners = new Set();

  // 实例变量
  let ws = null;
  let heartBeatTimer = null;
  let messageQueue = [];
  let isDestroyed = false;

  // 心跳管理
  const heartbeat = {
    start() {
      if (!config.enableHeartbeat || heartBeatTimer) return;

      heartBeatTimer = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          this.send();
        }
      }, config.heartbeatInterval);
    },

    stop() {
      if (heartBeatTimer) {
        clearInterval(heartBeatTimer);
        heartBeatTimer = null;
      }
    },

    send() {
      try {
        ws?.send(
          JSON.stringify({
            cmd: HEARTBEAT_COMMANDS.PING,
            timestamp: Date.now(),
          }),
        );
        if (config.debug) {
          console.log("[WebSocket] 心跳发送成功");
        }
      } catch (error) {
        handleError("心跳发送失败", error);
      }
    },

    isHeartbeatMessage(msg) {
      return msg && (msg.cmd === HEARTBEAT_COMMANDS.PONG || msg.cmd === HEARTBEAT_COMMANDS.PING_CODE);
    },
  };

  // 状态更新函数
  const updateState = (updates) => {
    Object.assign(wsState, updates);
    statusListeners.forEach((fn) => {
      try {
        fn(wsState);
      } catch (error) {
        console.error("[WebSocket] 状态监听器错误:", error);
      }
    });
  };

  // 错误处理
  const handleError = (message, error) => {
    console.error(`[WebSocket] ${message}:`, error);
    updateState({
      lastError: { message, error, timestamp: Date.now() },
    });

    errorListeners.forEach((fn) => {
      try {
        fn(message, error);
      } catch (err) {
        console.error("[WebSocket] 错误监听器异常:", err);
      }
    });
  };

  // 消息处理
  const processMessage = async (rawData) => {
    let textData;

    try {
      if (rawData instanceof Blob) {
        textData = await rawData.text();
      } else if (typeof rawData === "string") {
        textData = rawData;
      } else {
        return;
      }

      const msg = JSON.parse(textData);

      // 跳过心跳消息
      if (heartbeat.isHeartbeatMessage(msg)) {
        return;
      }

      // 分发消息给监听器
      listeners.forEach((fn) => {
        try {
          fn(msg);
        } catch (error) {
          handleError("监听器处理消息失败", error);
        }
      });
    } catch (error) {
      handleError("消息解析失败", error);
    }
  };

  // 发送消息队列
  const flushMessageQueue = () => {
    const queueLength = messageQueue.length;
    if (queueLength === 0) return;

    if (config.logQueueOperations) {
      console.log(`[WebSocket] 开始发送队列中的 ${queueLength} 条消息`);
    }

    let sentCount = 0;
    let failedCount = 0;

    while (messageQueue.length > 0 && ws?.readyState === WebSocket.OPEN) {
      const message = messageQueue.shift();
      try {
        ws.send(JSON.stringify(message));
        sentCount++;
      } catch (error) {
        handleError("队列消息发送失败", error);
        messageQueue.unshift(message); // 重新入队
        failedCount++;
        break;
      }
    }

    if (config.logQueueOperations) {
      if (sentCount > 0) {
        console.log(`[WebSocket] 成功发送 ${sentCount} 条队列消息`);
      }
      if (failedCount > 0) {
        console.warn(`[WebSocket] ${failedCount} 条消息发送失败，保留在队列中`);
      }
    }
  };

  // 初始化WebSocket连接
  const initializeSocket = () => {
    if (!browser || isDestroyed) return;

    try {
      ws = new ReconnectingWebSocket(url, [], config);

      ws.onopen = () => {
        updateState({
          status: CONNECTION_STATUS.CONNECTED,
          connected: true,
          reconnectAttempts: 0,
          lastError: null,
        });

        console.log("[WebSocket] 连接成功:", url);
        heartbeat.start();
        flushMessageQueue();
      };

      ws.onclose = (event) => {
        const attempts = wsState.reconnectAttempts + 1;
        
        // 检查是否达到最大重连次数
        const isMaxRetriesReached = attempts >= config.maxRetries;
        
        updateState({
          status: isMaxRetriesReached ? CONNECTION_STATUS.MAX_RETRIES_REACHED : CONNECTION_STATUS.DISCONNECTED,
          connected: false,
          reconnectAttempts: attempts,
        });

        // 记录待发送消息数量
        const queuedCount = messageQueue.length;

        // 构建关闭日志信息
        const closeInfo = {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        };

        // 根据配置决定是否包含队列信息
        if (config.enableDetailedQueueInfo) {
          closeInfo.queuedMessages = queuedCount;
        }

        if (isMaxRetriesReached) {
          console.error(`[WebSocket] 达到最大重连次数 (${config.maxRetries})，停止重连:`, closeInfo);
          // 达到最大重连次数时，停止自动重连
          ws.close();
        } else {
          console.warn(`[WebSocket] 连接关闭 (第${attempts}次):`, closeInfo);
        }

        // 停止心跳
        heartbeat.stop();

        // 处理连接关闭时的消息队列
        if (queuedCount > 0) {
          if (config.logQueueOperations) {
            console.log(`[WebSocket] 连接关闭时有 ${queuedCount} 条消息待发送，将在重连后自动发送`);
          }

          // 根据配置决定是否通知状态监听器队列状态
          if (config.includeQueueInStatus) {
            const statusUpdate = {
              ...wsState,
            };

            // 根据详细信息配置决定包含的内容
            if (config.enableDetailedQueueInfo) {
              statusUpdate.queuedMessages = queuedCount;
              statusUpdate.lastCloseEvent = {
                code: event.code,
                reason: event.reason,
                timestamp: Date.now(),
                hadQueuedMessages: true,
              };
            }

            statusListeners.forEach((fn) => {
              try {
                fn(statusUpdate);
              } catch (error) {
                console.error("[WebSocket] 状态监听器错误:", error);
              }
            });
          }
        }
      };

      ws.onerror = (event) => {
        updateState({
          status: CONNECTION_STATUS.ERROR,
          connected: false,
        });

        handleError("连接错误", {
          event,
          readyState: ws?.readyState,
          url,
        });
      };

      ws.onmessage = (event) => {
        processMessage(event.data);
      };
    } catch (error) {
      handleError("WebSocket初始化失败", error);
    }
  };

  // 发送消息
  const send = (data) => {
    if (!data) {
      console.warn("[WebSocket] 尝试发送空数据，已忽略");
      return false;
    }

    if (isDestroyed) {
      console.warn("[WebSocket] 连接已销毁，无法发送消息");
      return false;
    }

    if (wsState.connected && ws?.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(data));
        return true;
      } catch (error) {
        handleError("消息发送失败", error);
        return false;
      }
    } else {
      // 检查队列长度限制
      if (messageQueue.length >= config.maxQueueSize) {
        console.error(`[WebSocket] 队列已满 (${config.maxQueueSize})，无法添加新消息`);
        return false;
      }

      // 消息入队等待连接
      messageQueue.push(data);

      if (config.logQueueOperations) {
        console.warn(`[WebSocket] 连接未就绪，消息已入队 (队列长度: ${messageQueue.length})`);
      }
      return false;
    }
  };

  // 监听器管理
  const onMessage = (callback) => {
    if (typeof callback !== "function") {
      throw new Error("消息监听器必须是函数");
    }

    listeners.add(callback);
    return () => listeners.delete(callback);
  };

  const onError = (callback) => {
    if (typeof callback !== "function") {
      throw new Error("错误监听器必须是函数");
    }

    errorListeners.add(callback);
    return () => errorListeners.delete(callback);
  };

  const onStatusChange = (callback) => {
    if (typeof callback !== "function") {
      throw new Error("状态监听器必须是函数");
    }

    statusListeners.add(callback);
    return () => statusListeners.delete(callback);
  };

  // 重连控制
  const reconnect = () => {
    if (isDestroyed) return;

    console.log("[WebSocket] 手动重连...");
    
    // 手动重连时重置重连次数
    updateState({
      reconnectAttempts: 0,
      status: CONNECTION_STATUS.CONNECTING,
    });
    
    ws?.reconnect?.();
  };

  // 关闭连接
  const close = (code = 1000, reason = "正常关闭") => {
    console.log("[WebSocket] 正在关闭连接...");

    isDestroyed = true;
    heartbeat.stop();

    if (ws) {
      ws.close(code, reason);
      ws = null;
    }

    // 清理队列和监听器
    messageQueue.length = 0;
    listeners.clear();
    errorListeners.clear();
    statusListeners.clear();

    // 从缓存中移除
    sockets.delete(path);

    updateState({
      status: CONNECTION_STATUS.DISCONNECTED,
      connected: false,
    });
  };

  // 获取连接信息
  const getInfo = () => {
    const info = {
      url,
      state: { ...wsState },
      readyState: ws?.readyState,
      queueLength: messageQueue.length,
      listenersCount: listeners.size,
      maxRetries: config.maxRetries, // 添加最大重连次数信息
    };

    // 根据配置决定是否包含详细队列信息
    if (config.enableDetailedQueueInfo) {
      info.queue = [...messageQueue]; // 队列快照
      info.config = {
        maxQueueSize: config.maxQueueSize,
        logQueueOperations: config.logQueueOperations,
        includeQueueInStatus: config.includeQueueInStatus,
        maxRetries: config.maxRetries,
      };
    }

    return info;
  };

  // 获取队列状态
  const getQueueStatus = () => {
    const status = {
      length: messageQueue.length,
      isEmpty: messageQueue.length === 0,
      isFull: messageQueue.length >= config.maxQueueSize,
      maxSize: config.maxQueueSize,
    };

    // 根据配置决定是否包含详细消息信息
    if (config.enableDetailedQueueInfo) {
      status.messages = messageQueue.map((msg, index) => ({
        index,
        topic: msg.topic,
        timestamp: msg.timestamp || Date.now(),
        // 只在调试模式下包含完整消息内容
        ...(config.debug && { content: msg }),
      }));
    }

    return status;
  };

  // 清空消息队列
  const clearQueue = () => {
    const clearedCount = messageQueue.length;
    messageQueue.length = 0;

    if (config.logQueueOperations || config.debug) {
      console.log(`[WebSocket] 已清空 ${clearedCount} 条队列消息`);
    }

    return clearedCount;
  };

  // 创建socket实例
  const socket = {
    // 状态
    get state() {
      return wsState;
    },
    get connected() {
      return wsState.connected;
    },
    get status() {
      return wsState.status;
    },

    // 核心方法
    send,
    close,
    reconnect,

    // 监听器
    onMessage,
    onError,
    onStatusChange,

    // 工具方法
    getInfo,
    getQueueStatus,
    clearQueue,
  };

  // 初始化连接
  if (browser) {
    initializeSocket();

    // 页面卸载时清理
    const cleanup = () => close(1000, "页面卸载");
    window.addEventListener("beforeunload", cleanup);
    window.addEventListener("pagehide", cleanup);
  } else {
    // 服务端环境的占位实现
    socket.send = (data) => {
      console.warn("[WebSocket] 服务端环境不支持WebSocket", data);
      return false;
    };
  }

  // 缓存实例
  sockets.set(path, socket);

  return socket;
};
